/**
 * Audio utilities for OpenAI Realtime API
 *
 * OpenAI Realtime API expects:
 * - PCM16 format (16-bit linear PCM)
 * - 24kHz sample rate
 * - Mono channel
 * - Base64 encoded
 */

/**
 * Create audio context for audio processing
 */
export function createAudioContext(): AudioContext {
  return new AudioContext({ sampleRate: 24000 });
}

/**
 * Convert Float32Array audio data to PCM16 Int16Array
 */
export function floatTo16BitPCM(float32Array: Float32Array): Int16Array {
  const buffer = new ArrayBuffer(float32Array.length * 2);
  const view = new DataView(buffer);

  for (let i = 0; i < float32Array.length; i++) {
    // Clamp to [-1, 1] and convert to 16-bit integer
    const s = Math.max(-1, Math.min(1, float32Array[i]));
    view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
  }

  return new Int16Array(buffer);
}

/**
 * Convert Int16Array PCM16 audio to base64 string
 */
export function pcm16ToBase64(pcm16: Int16Array): string {
  const uint8 = new Uint8Array(pcm16.buffer);
  let binary = '';
  for (let i = 0; i < uint8.length; i++) {
    binary += String.fromCharCode(uint8[i]);
  }
  return btoa(binary);
}

/**
 * Convert base64 string to Int16Array PCM16 audio
 */
export function base64ToPCM16(base64: string): Int16Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Int16Array(bytes.buffer);
}

/**
 * Convert Int16Array PCM16 to Float32Array for Web Audio API
 */
export function pcm16ToFloat32(pcm16: Int16Array): Float32Array {
  const float32 = new Float32Array(pcm16.length);
  for (let i = 0; i < pcm16.length; i++) {
    float32[i] = pcm16[i] / (pcm16[i] < 0 ? 0x8000 : 0x7FFF);
  }
  return float32;
}

/**
 * Audio playback queue for smooth streaming
 */
export class AudioQueue {
  private audioContext: AudioContext;
  private queue: AudioBuffer[] = [];
  private isPlaying = false;
  private nextStartTime = 0;

  constructor() {
    this.audioContext = createAudioContext();
  }

  /**
   * Add audio chunk to the queue
   */
  async addChunk(pcm16Data: Int16Array) {
    const float32Data = pcm16ToFloat32(pcm16Data);

    // Create audio buffer
    const audioBuffer = this.audioContext.createBuffer(
      1, // mono
      float32Data.length,
      this.audioContext.sampleRate
    );

    audioBuffer.getChannelData(0).set(float32Data);

    this.queue.push(audioBuffer);

    if (!this.isPlaying) {
      this.playNext();
    }
  }

  private playNext() {
    if (this.queue.length === 0) {
      this.isPlaying = false;
      return;
    }

    this.isPlaying = true;
    const buffer = this.queue.shift()!;

    const source = this.audioContext.createBufferSource();
    source.buffer = buffer;
    source.connect(this.audioContext.destination);

    // Schedule playback
    const startTime = Math.max(this.nextStartTime, this.audioContext.currentTime);
    source.start(startTime);
    this.nextStartTime = startTime + buffer.duration;

    // Play next chunk when this one ends
    source.onended = () => {
      this.playNext();
    };
  }

  /**
   * Clear the queue and stop all playback immediately.
   * Closing the AudioContext cancels any already-scheduled
   * AudioBufferSourceNodes so nothing keeps playing.
   */
  clear() {
    this.queue = [];
    this.isPlaying = false;
    this.nextStartTime = 0;
    try {
      this.audioContext.close();
    } catch {
      /* already closed */
    }
  }

  /**
   * Get audio context for other operations
   */
  getContext(): AudioContext {
    return this.audioContext;
  }
}

/**
 * Resample audio to 24kHz if needed
 */
export async function resampleTo24kHz(
  audioBuffer: AudioBuffer
): Promise<AudioBuffer> {
  if (audioBuffer.sampleRate === 24000) {
    return audioBuffer;
  }

  // Create offline context at 24kHz
  const offlineContext = new OfflineAudioContext(
    1, // mono
    Math.ceil(audioBuffer.duration * 24000),
    24000
  );

  // Create buffer source
  const source = offlineContext.createBufferSource();
  source.buffer = audioBuffer;
  source.connect(offlineContext.destination);
  source.start(0);

  // Render resampled audio
  return await offlineContext.startRendering();
}
